import React, { useMemo } from "react";
import PropTypes from "prop-types";

import FlexTable from "./ui-components/FlexTable";
import StatusPill from "../views/Budgets/components/StatusPill";
import {
	formatAmountPrimary,
	formatAmountSecondary,
	formatPeriodLabel,
	formatScopeSummary,
	formatRelativeTime,
} from "../views/Budgets/formatters";
import {
	formatMonthlyForecast,
	forecastAsOfTitle,
} from "../views/Budgets/utils/budgetForecastColumn";

// ── Main component ───────────────────────────────────────────────
// Budget-specific column definitions rendered through the shared FlexTable. The page owns the
// card chrome (TableCard) and the empty state; this component returns null when there's no data.

const BudgetsTable = ({ data, onRowClick, onOpenDetails, forecastsById = {} }) => {
	// The row itself previews the budget (the page opens a drawer); the Details button is
	// the direct route to the budget's own page. Callers that don't split the two get the
	// old behaviour, where both land on the same handler.
	const openDetails = onOpenDetails || onRowClick;
	const columnDefs = useMemo(
		() => [
			// ── Budget (name + scope subtitle) ──
			{
				accessor: "name",
				Header: () => <span>Budget</span>,
				Cell: ({ row }) => {
					const { name, scope_summary } = row.original;
					const hasSummary = scope_summary?.text != null && String(scope_summary.text).trim() !== "";
					const scopeText = formatScopeSummary(scope_summary);
					return (
						<div className="flex flex-col gap-0.5 min-w-0">
							<span className="font-medium text-text truncate">
								{name}
							</span>
							<span
								className={`text-xs truncate ${hasSummary ? "text-text-2" : "text-text-2 italic"}`}
								title={hasSummary ? scope_summary.text : undefined}
							>
								{scopeText}
							</span>
						</div>
					);
				},
				flex: 1,
				minWidth: 260,
			},

			// ── Amount (primary + secondary) ──
			{
				accessor: "amount_cents",
				Header: () => <span>Amount</span>,
				Cell: ({ row }) => {
					const { total_amount_cents, period_type, currency, periods } = row.original;
					const periodCount = periods?.length || null;
					const primary = formatAmountPrimary(total_amount_cents, period_type, currency, periodCount);
					const secondary = formatAmountSecondary(total_amount_cents, period_type, currency);
					return (
						<div className="flex flex-col gap-0.5 min-w-0">
							<span className="font-medium text-text truncate">
								{primary}
							</span>
							{secondary && (
								<span className="text-xs text-text-2 truncate">
									{secondary}
								</span>
							)}
						</div>
					);
				},
				width: 200,
				minWidth: 180,
				maxWidth: 260,
			},

			// ── Forecast / month ──
			// Placed directly after Amount: the whole point of the column is comparing the
			// two, and normalizeToMonthly is applied to both so they are on one basis.
			{
				id: "forecast_monthly",
				Header: () => <span>Forecast</span>,
				Cell: ({ row }) => {
					const { id, period_type, currency } = row.original;
					const fc = forecastsById[String(id)];
					const text = formatMonthlyForecast(fc, period_type, currency);
					// "-" rather than a zero: a budget the evaluation job has not reached yet
					// is unknown, and claiming it is forecast to spend nothing is worse than
					// admitting we do not know.
					if (!text) {
						return <span className="text-text-2">-</span>;
					}
					return (
						<span className="font-medium text-text truncate" title={forecastAsOfTitle(fc)}>
							{text}
						</span>
					);
				},
				width: 200,
				minWidth: 180,
				maxWidth: 260,
			},

			// ── Period ──
			{
				accessor: "period_type",
				Header: () => <span>Period</span>,
				Cell: ({ value }) => (
					<span className="text-text">
						{formatPeriodLabel(value)}
					</span>
				),
				width: 100,
				minWidth: 100,
				maxWidth: 130,
			},

			// ── Status ──
			{
				accessor: "status",
				align: "center",
				Header: () => <span>Status</span>,
				Cell: ({ value }) => (
					<StatusPill status={value?.toLowerCase()} />
				),
				width: 120,
				minWidth: 110,
				maxWidth: 140,
			},

			// ── Last updated ──
			{
				accessor: "updated_at",
				Header: () => <span>Updated</span>,
				Cell: ({ value }) => (
					<span
						className="text-text-2"
						title={value ? new Date(value).toISOString() : undefined}
					>
						{formatRelativeTime(value)}
					</span>
				),
				width: 130,
				minWidth: 120,
				maxWidth: 160,
			},

			// ── Action ──
			// Always visible (not hover-revealed): with the row click opening a preview
			// drawer, this is the only direct route to the budget's own page, so it has to
			// be discoverable without hovering.
			{
				id: "action",
				align: "right",
				Header: () => null,
				Cell: ({ row }) => (
					<button
						type="button"
						onClick={(e) => {
							e.stopPropagation();
							openDetails(row.original.id);
						}}
						aria-label={`Open details for ${row.original.name}`}
						className="text-sm font-semibold text-primary transition-colors hover:text-primary-hover"
					>
						Details
					</button>
				),
				width: 110,
				minWidth: 100,
				maxWidth: 140,
			},
		],
		[openDetails, forecastsById]
	);

	if (!data || data.length === 0) {
		return null; // Empty state is handled by the parent page
	}

	return (
		<FlexTable
			columns={columnDefs}
			data={data}
			onRowClick={(id) => onRowClick(id)}
			getRowId={(b) => b.id}
			rowAriaLabel={(b) => `Preview budget ${b.name}`}
		/>
	);
};

BudgetsTable.propTypes = {
	data: PropTypes.array,
	// Latest evaluated projection per budget, keyed by budget id as a string. Optional:
	// the table renders "-" in the forecast column until it arrives.
	forecastsById: PropTypes.object,
	// Row click — the page's preview affordance.
	onRowClick: PropTypes.func,
	// The Details button. Defaults to onRowClick when a caller doesn't split the two.
	onOpenDetails: PropTypes.func,
};

export default BudgetsTable;
